
import React, { useState } from 'react';
import { auth, db } from '../../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { LogIn, ArrowLeft, Building2, Loader2, ShieldCheck, UserPlus, CheckCircle2 } from 'lucide-react';
import { MemberModel, UserRole } from '../../models';

interface BranchLoginProps {
    onBack: () => void;
    onSuccess: (user: any) => void;
}

const BranchLogin: React.FC<BranchLoginProps> = ({ onBack, onSuccess }) => {
    const [mode, setMode] = useState<'login' | 'register' | 'reset'>('login');
    const [loading, setLoading] = useState(false);

    // Form States
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Register Only States
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const role: UserRole = 'teacher';

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const result = await signInWithEmailAndPassword(auth, email.trim(), password);
            onSuccess(result.user);
        } catch (error: any) {
            console.error("Login failed:", error);
            if (error.code === 'auth/user-not-found') {
                alert('가입되지 않은 이메일입니다. 회원가입을 먼저 진행해주세요.');
            } else if (error.code === 'auth/wrong-password') {
                alert('비밀번호가 올바르지 않습니다.');
            } else if (error.code === 'auth/invalid-credential') {
                alert('이메일 또는 비밀번호가 올바르지 않습니다.');
            } else if (error.code === 'auth/too-many-requests') {
                alert('로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.');
            } else {
                alert(`로그인 실패: ${error.message}`);
            } //
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            alert('비밀번호가 일치하지 않습니다.');
            return;
        }

        // Email Validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            alert('유효한 이메일 주소를 입력해주세요.');
            return;
        }

        // Phone Validation
        const cleanPhone = phone.replace(/-/g, '').trim();
        if (!cleanPhone.startsWith('010') || cleanPhone.length !== 11) {
            alert('연락처는 010으로 시작하는 11자리 숫자로 입력해주세요.');
            return;
        }

        setLoading(true);
        try {
            // 1. Create Auth User
            const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
            const user = result.user;

            // 2. Update Profile Display Name
            await updateProfile(user, { displayName: name });

            // 3. Create Member Document
            const newMember: MemberModel = {
                id: user.uid,
                name: name,
                email: email,
                role: role,
                status: 'active', // UserStatus: 'active' | 'suspended'
                // Wait, 'status' in Types is 'active' | 'suspended'. Let's use 'active'.
                // However, without a branchId or policy, they might get Access Denied.
                // That's fine, the ManagerRoot/StaffRoot handles the "RegistrationForm" (Branch Connect).
                phone: cleanPhone,
                joinedAt: Date.now(),
                accessPolicy: { type: 'PERIOD', dueAt: 0 }, // Default
                grade: '', // Not applicable
                dueAt: 0,
            } as MemberModel;

            await setDoc(doc(db, 'members', user.uid), newMember);

            alert('회원가입이 완료되었습니다.');
            onSuccess(user);

        } catch (error: any) {
            console.error("Registration failed:", error);
            if (error.code === 'auth/email-already-in-use') {
                alert('이미 사용 중인 이메일입니다.');
            } else if (error.code === 'auth/weak-password') {
                alert('비밀번호는 6자리 이상이어야 합니다.');
            } else {
                alert(`가입 실패: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            alert('이메일을 입력해주세요.');
            return;
        }
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email.trim());
            alert('비밀번호 재설정 이메일이 발송되었습니다.\n메일함(스팸메일함 포함)을 확인해주세요.');
            setMode('login');
        } catch (error: any) {
            console.error("Reset failed:", error);
            if (error.code === 'auth/user-not-found') {
                alert('가입되지 않은 이메일입니다.');
            } else {
                alert(`전송 실패: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 md:p-8 relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                <div className="absolute top-1/4 -left-20 w-80 h-80 bg-indigo-500/10 blur-[100px] rounded-full"></div>
                <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-violet-500/10 blur-[100px] rounded-full"></div>
            </div>

            <button
                onClick={onBack}
                className="absolute top-4 left-4 md:top-8 md:left-8 flex items-center gap-2 text-slate-500 hover:text-white transition-colors font-bold text-xs uppercase tracking-widest group z-10"
            >
                <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center group-hover:bg-slate-700 transition-all">
                    <ArrowLeft size={16} />
                </div>
                홈으로 돌아가기
            </button>

            <div className={`w-full max-w-md bg-slate-800 rounded-[2rem] border border-slate-700/50 p-8 md:p-12 shadow-2xl relative animate-in fade-in zoom-in duration-500 transition-all ${mode === 'register' ? 'max-w-lg' : ''}`}>
                <div className="flex flex-col items-center text-center space-y-8">
                    <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-900/50">
                        {mode === 'login' ? <Building2 size={32} /> : mode === 'register' ? <UserPlus size={32} /> : <ShieldCheck size={32} />}
                    </div>

                    <div className="space-y-2">
                        <h2 className="text-2xl font-black text-white tracking-tight">
                            {mode === 'login' ? '선생님 로그인' : mode === 'register' ? '선생님 계정 생성' : '비밀번호 재설정'}
                        </h2>
                        <p className="text-slate-400 font-medium text-sm">
                            {mode === 'login'
                                ? '등록된 선생님 계정으로 로그인해주세요.'
                                : mode === 'register'
                                    ? '새로운 선생님 계정을 개설합니다.'
                                    : '가입하신 이메일로 비밀번호 재설정 링크를 보내드립니다.'}
                        </p>
                    </div>

                    <form onSubmit={mode === 'login' ? handleLogin : mode === 'register' ? handleRegister : handleResetPassword} className="w-full space-y-4">
                        {mode === 'register' && (
                            <>
                                <div className="space-y-1.5 text-left">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">이름</label>
                                    <input
                                        required
                                        className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                                        placeholder="홍길동"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1.5 text-left">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">연락처</label>
                                    <input
                                        required
                                        type="tel"
                                        className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                                        placeholder="010-0000-0000"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                    />
                                </div>
                                {/* Preset role is 'teacher' */}
                            </>
                        )}

                        <div className="space-y-1.5 text-left">
                            <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">이메일</label>
                            <input
                                type="email"
                                required
                                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                                placeholder="name@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        {mode !== 'reset' && (
                            <div className="space-y-1.5 text-left">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">비밀번호</label>
                                <input
                                    type="password"
                                    required
                                    className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                {mode === 'register' && <p className="text-xs text-slate-500 pl-1">6자리 이상 입력해주세요.</p>}
                            </div>
                        )}

                        {mode === 'login' && (
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => { setMode('reset'); }}
                                    className="text-xs font-bold text-slate-400 hover:text-indigo-400 transition-colors"
                                >
                                    비밀번호를 잊으셨나요?
                                </button>
                            </div>
                        )}

                        {mode === 'register' && (
                            <div className="space-y-1.5 text-left">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">비밀번호 확인</label>
                                <input
                                    type="password"
                                    required
                                    className={`w-full px-4 py-3 rounded-xl bg-slate-900 border ${password && confirmPassword && password !== confirmPassword ? 'border-red-500 focus:ring-red-500' : 'border-slate-700 focus:ring-indigo-500'} text-white placeholder:text-slate-600 focus:ring-2 outline-none transition-all text-sm font-medium`}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                {password && confirmPassword && password !== confirmPassword && (
                                    <p className="text-xs text-red-500 pl-1">비밀번호가 일치하지 않습니다.</p>
                                )}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || (mode === 'register' && password !== confirmPassword)}
                            className="w-full bg-indigo-600 text-white py-4 rounded-xl font-black text-base shadow-lg shadow-indigo-900/20 hover:bg-indigo-500 transition-all active:scale-95 disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
                        >
                            {loading ? <Loader2 className="animate-spin" size={20} /> : (mode === 'login' ? '로그인' : mode === 'register' ? '계정 생성하기' : '인증 메일 전송')}
                        </button>
                    </form>

                    <div className="w-full border-t border-slate-700/50 pt-6">
                        <button
                            onClick={() => {
                                if (mode === 'reset') {
                                    setMode('login');
                                } else {
                                    setMode(mode === 'login' ? 'register' : 'login');
                                }
                                setEmail(''); setPassword(''); setConfirmPassword(''); setName(''); setPhone('');
                            }}
                            className="text-slate-400 hover:text-white text-sm font-bold transition-colors"
                        >
                            {mode === 'login' ? '계정이 없으신가요? 선생님 회원가입' : mode === 'register' ? '이미 계정이 있으신가요? 로그인' : '로그인 화면으로 돌아가기'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="mt-8 text-center">
                <p className="text-xs text-slate-500 flex items-center justify-center gap-2">
                    <ShieldCheck size={14} />
                    보안 접속 중
                </p>
            </div>
        </div>
    );
};

export default BranchLogin;
